/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import { useTabManagerContext } from '../context/TabManagerContext';

export const useBlockRename = () => {
    const {
        editorRef,
        astParser,
        tabContentRef,
        tabOriginalContentRef,
        blockCacheRef,
        setTabs,
        setActiveTabId,
        activeTabIdRef,
        tabsRef,
    } = useTabManagerContext();

    const performBlockRename = (oldBlockName: string, newBlockName: string, cacheCurrentTab: () => void) => {
        if (!editorRef.current) return;

        const currentCode = editorRef.current.getValue();

        // Update the tab name and cache with the new block name
        const oldTabId = `block-${oldBlockName}`;
        const newTabId = `block-${newBlockName}`;

        // Cache current content under the new block name
        cacheCurrentTab();
        const cachedContent = blockCacheRef.current.get(oldBlockName);
        if (cachedContent) {
            blockCacheRef.current.set(newBlockName, cachedContent);
            blockCacheRef.current.delete(oldBlockName);
        }

        // Update tab content refs
        tabContentRef.current.set(newTabId, currentCode);
        tabContentRef.current.delete(oldTabId);

        const originalContent = tabOriginalContentRef.current.get(oldTabId);
        if (originalContent) {
            tabOriginalContentRef.current.set(newTabId, originalContent);
            tabOriginalContentRef.current.delete(oldTabId);
        }

        // Update the tab in the tabs array
        setTabs((prevTabs) => {
            const updatedTabs = prevTabs.map((tab) => {
                if (tab.id === oldTabId) {
                    return { ...tab, id: newTabId, name: newBlockName };
                }
                return tab;
            });
            tabsRef.current = updatedTabs;
            return updatedTabs;
        });

        // Update active tab reference
        setActiveTabId(newTabId);
        activeTabIdRef.current = newTabId;
    };

    const checkForBlockRename = (
        tabId: string,
        cacheCurrentTab: () => void,
        switchTab: (inputTabId: string) => void,
    ): boolean => {
        // Check if we're switching from a block tab and if the function name has changed
        if (activeTabIdRef.current.startsWith('block-') && editorRef.current) {
            const currentCode = editorRef.current.getValue();
            const blockFunctionInfo = astParser.extractBlockFunctionInfo(currentCode);
            const expectedBlockName = activeTabIdRef.current.substring(6); // Remove 'block-' prefix

            if (blockFunctionInfo) {
                // Check if the function name has changed
                if (blockFunctionInfo.name !== expectedBlockName) {
                    // Automatically rename the block
                    performBlockRename(expectedBlockName, blockFunctionInfo.name, cacheCurrentTab);

                    // Now switch to the pending tab
                    switchTab(tabId);
                    return true; // Tab switch handled
                }
            }
        }
        return false; // No rename needed, proceed with normal tab switch
    };

    return {
        checkForBlockRename,
    };
};
